import {Collapse} from "@mui/material";
import {PracticeApptReason} from "@services/monolith/appointmentReasons";
import {ApptSlot} from "@services/monolith/availability";
import React, {RefObject} from "react";

import {QueryState} from "../../../hooks/useQueryController";
import {useTranslation} from "../../../i18n";
import {getSchedulingLink} from "../../../page-containers/booking/utils/bookingQuery";
import ScrollContainer from "../../../page-containers/get-care/components/ScrollContainer";
import {RootStateLocation} from "../../../store/types";
import {s3ImageSource} from "../../../useS3ImgSrc";
import {compact} from "../../../utils/arrays";
import ClinicListRow from "./ClinicListRow";
import {sendClinicListRowClickEvent, sendInitiateWebSchedulingEvent} from "./events";
import {LocSlotPair} from "./usePairLocsWithSlots";
import VirtualRow from "./VirtualRow";

type ListProps = {
  isSm: boolean;
  loading?: boolean;
  apptReason: PracticeApptReason;
  locations: LocSlotPair<RootStateLocation>[];
  firstLocRef: RefObject<HTMLLIElement>;
  soonestVirtualSlotQuery: QueryState<ApptSlot>;
  viewId?: string;
};

const getLocationImages = (location: RootStateLocation) =>
  // @ts-expect-error TS2532: Object is possibly 'undefined'.
  location?.images.slice(0, 2).map(img => ({src: s3ImageSource(img.imageId), alt: null})) || [];

const LocationList = ({
  isSm,
  loading,
  apptReason,
  locations,
  firstLocRef,
  soonestVirtualSlotQuery,
  viewId,
}: ListProps): React.ReactElement => {
  const {t} = useTranslation();
  const isVirtualOnly = locations.length === 0 && !loading;

  return (
    <ScrollContainer maxHeight="calc(82vh - 5vw)" disabled={isSm} containerTag="ul">
      {locations.map(({location, soonestSlot, specialtyId}, i) => {
        const isFirstLoc = i === 0;
        const images = isFirstLoc ? getLocationImages(location) : [];

        const value = t("Book");

        const {bookingLink, flowId, WEB_SCHEDULING_ENABLED} = getSchedulingLink({
          specialtyIds: compact([specialtyId]),
          webSchedulingQuery: {
            locationSlug: location.slug,
            appointmentReasonSlug: apptReason.slug,
            initialCalendarTime: soonestSlot?.time,
            specialtyId,
          },
          patientAppSchedulingQuery: {
            practiceId: location.practiceId,
            locationId: location.id,
            apptReasonId: apptReason.id,
            specialtyId,
          },
        });

        const onClick = () => {
          if (soonestSlot) {
            sendClinicListRowClickEvent(
              value,
              viewId,
              location.id,
              i + 1,
              soonestSlot?.time,
              location.dist,
              flowId || undefined,
            );

            if (WEB_SCHEDULING_ENABLED) {
              sendInitiateWebSchedulingEvent(flowId);
            }
          }
        };

        return (
          <Collapse in appear key={location.id}>
            <ClinicListRow
              href={soonestSlot ? bookingLink : undefined}
              // @ts-expect-error TS2322: Type '{ src: string; alt: null; }[]' is not assignable to type '{ src: string; alt?: string | undefined; }[]'.
              images={images}
              loading={loading}
              ref={isFirstLoc ? firstLocRef : null}
              key={location.id}
              location={location}
              soonestSlotTime={soonestSlot?.time}
              clampLocName
              onClick={onClick}
            />
          </Collapse>
        );
      })}
      <VirtualRow
        soonestVirtualSlotQuery={soonestVirtualSlotQuery}
        hideImages={!isVirtualOnly}
        apptReason={apptReason}
        rank={locations.length}
        viewId={viewId}
      />
    </ScrollContainer>
  );
};

export default React.memo(LocationList);
